/**
 * Settings ▸ SDMS ▸ Scientific Data Table — the grid's row model and the pure
 * rules the page applies before it calls the API. The server
 * (`ScientificDataRules`) is authoritative; these mirror it so the user is told
 * before the round trip.
 */

export const SDT_PAGE_ID = 'set-sdms-scientificdata';
export const SDT_FORM_NAME = 'Scientific Data Table';
export const SDT_BASE = '/api/v1/sdms-scientific-data';

export const SDT_TABS = [
  { label: 'Raw Data', value: 'RawDataTable' },
  { label: 'Calibration', value: 'CalibrationTable' },
] as const;
export type SdtResultType = (typeof SDT_TABS)[number]['value'];

/** One grid row. `id` is null for a row added with Add and not saved yet. */
export interface SdtRow {
  /** Stable grid key — `r<id>` for a stored row, `n<seq>` for a new one. */
  key: string;
  id: number | null;
  fieldName: string;
  captionEn: string;
  captionCn: string;
  dataType: string;
  common: boolean;
  saveInTable: boolean;
}

export type SdtEditable = Pick<SdtRow, 'fieldName' | 'captionEn' | 'captionCn' | 'dataType' | 'common' | 'saveInTable'>;

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** API row → grid row. */
export function toSdtRow(d: Record<string, unknown>): SdtRow {
  const id = Number(d.id);
  return {
    key: `r${id}`,
    id,
    fieldName: str(d.fieldName),
    captionEn: str(d.captionEn),
    captionCn: str(d.captionCn),
    dataType: str(d.dataType),
    common: d.common === true,
    saveInTable: d.saveInTable === true,
  };
}

/** The row Add puts in the grid. Legacy ticks Save In Table on a new Raw Data row. */
export function newSdtRow(seq: number, resultType: SdtResultType): SdtRow {
  return {
    key: `n${seq}`, id: null, fieldName: '', captionEn: '', captionCn: '', dataType: '',
    common: false, saveInTable: resultType === 'RawDataTable',
  };
}

const same = (a: SdtRow, b: SdtRow) =>
  a.fieldName.trim() === b.fieldName.trim()
  && a.captionEn.trim() === b.captionEn.trim()
  && a.captionCn.trim() === b.captionCn.trim()
  && a.dataType.trim().toLowerCase() === b.dataType.trim().toLowerCase()
  && a.common === b.common
  && a.saveInTable === b.saveInTable;

/** The rows a Save sends: every new row, and every stored row that differs from what was loaded. */
export function changedSdtRows(rows: SdtRow[], loaded: Map<string, SdtRow>): SdtRow[] {
  return rows.filter((r) => {
    if (r.id == null) return true;
    const o = loaded.get(r.key);
    return !o || !same(r, o);
  });
}

/** First problem in the rows being saved, or null. Wording follows the server. */
export function firstSdtProblem(changed: SdtRow[], resultType: SdtResultType): string | null {
  const seen = new Set<string>();
  for (const r of changed) {
    const name = r.fieldName.trim();
    if (!name) return 'Fieldname should not be empty.';
    const k = name.toLowerCase();
    if (seen.has(k)) return `FieldName '${name}' is entered more than once.`;
    seen.add(k);
    if (resultType === 'RawDataTable' && r.saveInTable && !r.dataType.trim())
      return `DataType should not be empty for '${name}', because Save In Table is ticked.`;
  }
  return null;
}

/** Row → the body the API expects. */
export function toSdtInput(r: SdtRow) {
  return {
    id: r.id,
    fieldName: r.fieldName.trim(),
    captionEn: r.captionEn.trim() || null,
    captionCn: r.captionCn.trim() || null,
    dataType: r.dataType.trim() || null,
    common: r.common,
    saveInTable: r.saveInTable,
  };
}
